import { useEffect, useState } from 'react';
import { usePersonalInformationError } from '../providers/PersonalInformationErrorProvider';
import { usePersonalInformation } from '../providers/PersonalInformationProvider';
import { useHomeAddressError } from '../providers/HomeAddressErrorProvider';
import { useHomeAddress } from '../providers/HomeAddressProvider';
import { generateEmailFormatErrorMessage, generateErrorMessage } from '../../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../../utils/error-message-flag';
import { isValidEmailFormat } from '../../../utils/cob-utils';

const usePushupMessages = () => {
    const { personalInformationError } = usePersonalInformationError();
    const { personalInformation } = usePersonalInformation();
    const { homeAddressError } = useHomeAddressError();
    const [messages, setMessages] = useState<string[]>([]);
    const { homeAddress, showHomeAddress } = useHomeAddress();
    const emailErrorType = personalInformationError.EmailAddress.errorType
        ?? (!personalInformation.EmailAddress
            ? 'RequiredField'
            : !isValidEmailFormat(personalInformation.EmailAddress)
                ? 'NotValid'
                : 'ThirdParty');

    const errorEntryMap = {
        FirstName: generateErrorMessage('<a href="#firstName-field-container" id="cta-firstName">First Name</a>', personalInformation['FirstName'] === '' ? 'missingValue' : null),
        LastName: generateErrorMessage('<a href="#lastName-field-container" id="cta-lastName">Last Name</a>', personalInformation['LastName'] === '' ? 'missingValue' : null),
        MailingAddressLine1: generateErrorMessage(`<a href="#mailingAddress-field-container" id="cta-mailingAddress">Mailing ${personalInformation['MailingAddressLine1'] === '' ? 'Street ' : ''}Address</a>`, personalInformation['MailingAddressLine1'] === '' ? 'missingValue' : 'thirdParty', 'address'),
        MailingCity: generateErrorMessage(`<a href="#mailingCity-field-container" id="cta-mailingCity">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}City</a>`, personalInformation['MailingCity'] === '' ? 'missingValue' : null),
        MailingState: generateErrorMessage(`<a href="#mailingState-field-container" id="cta-mailingState">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}State</a>`, personalInformation['MailingState'] === '' ? 'missingValue' : null),
        MailingZipCode: generateErrorMessage(`<a href="#mailingZipCode-field-container" id="cta-mailingZipCode">${!isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}ZIP Code</a>`, personalInformation['MailingZipCode'] === '' ? 'missingValue' : null),
        HomeAddressLine1: generateErrorMessage('<a href="#homeAddress-field-container" id="cta-homeAddress">Home Street Address</a>', homeAddress['HomeAddressLine1'] === '' ? 'missingValue' : null),
        HomeCity: generateErrorMessage('<a href="#homeCity-field-container" id="cta-homeCity">Home Address City</a>', homeAddress['HomeCity'] === '' ? 'missingValue' : null),
        HomeState: generateErrorMessage('<a href="#homeState-field-container" id="cta-homeState">Home Address State</a>', homeAddress['HomeState'] === '' ? 'missingValue' : null),
        HomeZipCode: generateErrorMessage('<a href="#homeZipCode-field-container" id="cta-homeZipCode">Home Address ZIP Code</a>', homeAddress['HomeZipCode'] === '' ? 'missingValue' : null),
        EmailAddress: !isNewErrorMessagingFlagActive
        ? 'Please provide your <a href="#emailAddress-field-container" id="cta-emailAddress">Email Address (email@domain.com)</a>'
        : emailErrorType === 'RequiredField'
            ? generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'missingValue') + ': email@domain.com'
            : emailErrorType === 'NotValid'
                ? generateEmailFormatErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>')
                : generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'thirdParty', 'Email'),
        Ssn: generateErrorMessage('<u><a href="#socialSecurity-field-container" id="cta-socialSecurity">Social Security Number</u>', personalInformation['Ssn'] === '' ? 'missingValue' : null),
        dob: isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#dob-field-container" id="cta-dob">Date of Birth</a>', ['', '//'].some(e => e === personalInformation['dob']) ? 'missingValue' : null) : 'Please provide your <a href="#dob-field-container" id="cta-dob">Date of Birth (MM-DD-YYYY)</a>',
    };

    useEffect(() => {
        clearHashFromUrl(true);     
    }, []);

    const clearHashFromUrl = (bypassCondition) => {
        if (messages.filter(message => message !== undefined).length > 1 || bypassCondition) {
            if (window.location.hash) {
                window.history.replaceState(
                    null,
                    '',
                    window.location.pathname + window.location.search);
            }        
        }
    };

    useEffect(() => {
        let errorMessaging = { ...personalInformationError };

        if (showHomeAddress) {
            errorMessaging = Object.assign(errorMessaging, homeAddressError);
        }

        setMessages(Object.entries(errorEntryMap).map(([key, message]) => {
            if (errorMessaging[key]?.isInError) {
                return message; 
            }
        }));
    }, [personalInformationError, personalInformation, homeAddressError, homeAddress, showHomeAddress]);

    return {
        messages, 
        clearHashFromUrl,
    };
};

export default usePushupMessages;
